import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiLogin, apiMe, setToken, getToken } from '../api';
import i18n, { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';

const AuthContext = createContext(null);

const syncLanguageWith = (lang) => {
  const short = (lang || DEFAULT_LANGUAGE).split('-')[0];
  const target = SUPPORTED_LANGUAGES.includes(short) ? short : DEFAULT_LANGUAGE;
  if (i18n.language !== target) {
    i18n.changeLanguage(target);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    apiMe()
      .then((u) => {
        setUser(u);
        if (u?.language) syncLanguageWith(u.language);
      })
      .catch(() => {
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password, role) => {
    const { token, user: u } = await apiLogin(email, password, role);
    setToken(token);
    setUser(u);
    if (u?.language) syncLanguageWith(u.language);
    return u;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('aihc_user');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, loading }),
    [user, login, logout, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
